import { ReportResult } from '@pages/MarketCurvesCalculation/store/types';
import { ReviewTradeModes } from '@pages/Pricers/components/ButtonActions/components/ApproveTradeForm/enum';
import {
  FiledDownloadItem,
  fetchUploadedFilesPayload,
} from '@pages/Pricers/components/ButtonActions/components/UpdateTrade/components/UploadFiles/types';
import {
  DownloadBreakageMatrixPayload,
  DownloadCVAAnalysisPayload,
  DownloadPortfolioStatementRequestPayload,
  DownloadTermSheetPayload,
  DownloadXVAAnalysisPayload,
} from '@pages/Pricers/components/ReportDownloadPanel/types';
import {
  GetFxForwardRateRequestPayload,
  GetFxForwardRateSuccessPayload,
  GetFxSpotRateRequestPayload,
  GetFxSpotRateSuccessPayload,
  GetTransactionByIdRequestPayload,
  GetTransactionByIdSuccessPayload,
  ReviewTradeRequestPayload,
  ReviewTradeSuccessPayload,
  SaveTradePayloadRequest,
  SaveTradePayloadSuccess,
  UpdateDeletePayloadRequest,
  UpdateTypeTradePayloadSuccess,
  ValueTradePayloadRequest,
  ValueTradePayloadSuccess,
} from '@pages/Pricers/store/pricer/actions';
import { DownloadValuationReportRequestPayload } from '@pages/TradeCenter/store/actions';
import { AxiosInstance, AxiosResponse } from 'axios';

import { Leg } from 'models/Leg';

import { ApiResponse } from '../../models/ApiResponse';

export default class PricerAPI {
  client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async postParseHedgeConfirm(formData: FormData): Promise<
    AxiosResponse<{
      success: boolean;
      data: { transaction_id: number } | null;
      error_msg?: string;
    }>
  > {
    return this.client.post('/v2/hedging/parse-hedge-confirm/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async postChatTradeBuilder(payload: {
    message: string;
    conversation_history: { role: 'user' | 'assistant'; content: string }[];
  }): Promise<
    AxiosResponse<{
      success: boolean;
      data: {
        message: string;
        ready: boolean;
        transaction_id?: number;
        transaction_ids?: number[];
      } | null;
      error_msg?: string;
    }>
  > {
    return this.client.post('/v2/hedging/chat-trade-builder/', payload);
  }

  async postBuildSchedule(payload: { legs: Leg[] }) {
    return this.client.post('/v2/hedging/build-date-schedule/', payload);
  }

  async fetchTrade({
    transactionId,
  }: GetTransactionByIdRequestPayload): Promise<
    AxiosResponse<GetTransactionByIdSuccessPayload>
  > {
    return this.client.get(`/v2/hedging/transaction/${transactionId}/`);
  }

  async postSaveTrade({
    data,
  }: SaveTradePayloadRequest): Promise<
    AxiosResponse<{ data: SaveTradePayloadSuccess }>
  > {
    return this.client.post('/v2/hedging/save-trade/', data);
  }

  async postUpdateTypeTrade({
    data,
  }: SaveTradePayloadRequest): Promise<
    AxiosResponse<{ data: UpdateTypeTradePayloadSuccess }>
  > {
    return this.client.post('/v2/review/transaction/', data);
  }

  async postDeleteTrade({
    transaction_id,
  }: UpdateDeletePayloadRequest): Promise<
    AxiosResponse<{ data: UpdateTypeTradePayloadSuccess }>
  > {
    return this.client.delete(`/v2/hedging/transaction/${transaction_id}/`);
  }

  async postValueTrade({
    data,
  }: ValueTradePayloadRequest): Promise<
    AxiosResponse<ValueTradePayloadSuccess>
  > {
    return this.client.post('/v2/hedging/value-trade/in-memory/', data);
  }

  async sendUploadedFilesFromS3(payload: fetchUploadedFilesPayload) {
    return this.client.post(
      `/v2/file-management/transaction/${payload.transaction_id}/files/`,
      payload,
    );
  }

  async getFilesListDownload({
    transaction_id,
  }: {
    transaction_id: number;
  }): Promise<AxiosResponse<ApiResponse<FiledDownloadItem[]>>> {
    return this.client.get(
      `/v2/file-management/transaction/${transaction_id}/files/`,
    );
  }

  async postReviewTrade(
    payload: ReviewTradeRequestPayload,
  ): Promise<AxiosResponse<ApiResponse<ReviewTradeSuccessPayload>>> {
    const reviewType =
      payload.type === ReviewTradeModes.Approve
        ? 'approve'
        : payload.type === ReviewTradeModes.Amend
          ? 'amend'
          : 'reject';

    return this.client.post(
      `/v2/review/approvals/${reviewType}/`,
      payload.data,
    );
  }

  async postFxSpotRate(
    payload: GetFxSpotRateRequestPayload,
  ): Promise<AxiosResponse<GetFxSpotRateSuccessPayload>> {
    return this.client.post('/v2/market-data/fx-spot-rate/', payload);
  }

  async postFxForwardRate(
    payload: GetFxForwardRateRequestPayload,
  ): Promise<AxiosResponse<GetFxForwardRateSuccessPayload>> {
    return this.client.post('/v2/market-data/fx-forward-rate/', payload);
  }

  async downloadCVAAnalysisReport(
    data: DownloadCVAAnalysisPayload,
  ): Promise<AxiosResponse<ApiResponse<ReportResult>>> {
    return this.client.post('/v2/reporting/download-bcva-report/', data);
  }

  async downloadTermSheetReport(
    data: DownloadTermSheetPayload,
  ): Promise<AxiosResponse<ApiResponse<ReportResult>>> {
    return this.client.post(
      '/v2/reporting/download-term-sheet-report-trades/',
      data,
    );
  }

  async downloadBreakageMatrixReport(
    data: DownloadBreakageMatrixPayload,
  ): Promise<AxiosResponse<ApiResponse<ReportResult>>> {
    return this.client.post(
      '/v2/reporting/download-breakage-matrix-report/',
      data,
    );
  }

  async downloadXVAAnalysisReport(
    data: DownloadXVAAnalysisPayload,
  ): Promise<AxiosResponse<ApiResponse<ReportResult>>> {
    return this.client.post('/v2/reporting/download-xva-report/', data);
  }

  async downloadPortfolioStatementReport(
    payload: DownloadPortfolioStatementRequestPayload,
  ) {
    return this.client.post(
      '/v2/reporting/portfolio-statement-report/download/',
      payload,
    );
  }

  async downloadValuationReport(
    payload: DownloadValuationReportRequestPayload,
  ) {
    return this.client.post(
      '/v2/reporting/valuation-report/download/',
      payload,
    );
  }

  async postReorderLegs({
    leg_order,
  }: {
    leg_order: string[];
  }): Promise<AxiosResponse<ApiResponse<{ message: string }>>> {
    return this.client.post('/v2/hedging/transaction-legs/reorder/', {
      leg_order,
    });
  }
}
